import React, { useCallback, useEffect, useRef } from "react";
import "leaflet/dist/leaflet.css";
import "./BothiesMap.css";
import { useDispatch, useSelector } from "react-redux";
import MapView from "./MapView";
import BothyBrowser from "./BothyBrowser";
import BothyDetails from "./BothyDetails";
import {
  createDefaultFilters,
  NATURAL_FEATURE_OPTIONS
} from "../../constants/bothies";
import {
  clearFilters,
  clearSelectedBothy,
  selectAllBothiesCount,
  selectAvailableBortleClasses,
  selectAvailableBothyFeatures,
  selectFilters,
  selectSearchTerm,
  selectSelectedBothy,
  selectShowFilters,
  selectVisibleBothies,
  setBortleClassFilter,
  setElevationFilter,
  setRoadDistanceFilter,
  setSearchTerm,
  setSelectedBothy,
  setWeatherFilter,
  hydrateFilters,
  toggleBothyFeatureFilter,
  toggleFilters,
  toggleNaturalFeatureFilter
} from "../../store/bothiesSlice";
import {
  selectWeatherDayOptions,
  selectWeatherRanges,
  selectWeatherSummaryForBothy,
  selectWeatherUnits
} from "../../store/weatherSlice";

const BothiesMap = () => {
  const dispatch = useDispatch();
  const visibleBothies = useSelector(selectVisibleBothies);
  const allBothiesCount = useSelector(selectAllBothiesCount);
  const selectedBothy = useSelector(selectSelectedBothy);
  const searchValue = useSelector(selectSearchTerm);
  const showFilters = useSelector(selectShowFilters);
  const filters = useSelector(selectFilters);
  const availableBothyFeatures = useSelector(selectAvailableBothyFeatures);
  const availableBortleClasses = useSelector(selectAvailableBortleClasses);
  const weatherDayOptions = useSelector(selectWeatherDayOptions);
  const weatherUnits = useSelector(selectWeatherUnits);
  const weatherRanges = useSelector(selectWeatherRanges);
  const weatherState = useSelector(state =>
    selectWeatherSummaryForBothy(state, selectedBothy?.id)
  );

  const hasHydratedFromUrl = useRef(false);

  // Hydrate filters/search from URL on first render so links can share state
  useEffect(
    () => {
      if (hasHydratedFromUrl.current) return;
      const parsed = parseFiltersFromSearch(window.location.search);
      if (parsed.hasParams) {
        dispatch(
          hydrateFilters({
            filters: parsed.filters,
            searchTerm: parsed.searchTerm,
            showFilters: true
          })
        );
      }
      hasHydratedFromUrl.current = true;
    },
    [dispatch]
  );

  // Keep URL query params in sync with current filters & search
  useEffect(
    () => {
      if (!hasHydratedFromUrl.current) return;
      const query = buildSearchFromFilters(filters, searchValue);
      const newUrl = `${window.location.pathname}${query ? `?${query}` : ""}${
        window.location.hash
      }`;
      if (
        newUrl !==
        window.location.pathname + window.location.search + window.location.hash
      ) {
        window.history.replaceState({}, "", newUrl);
      }
    },
    [filters, searchValue]
  );

  const handleBothyClick = useCallback(
    bothy => {
      dispatch(setSelectedBothy(bothy.id));
    },
    [dispatch]
  );

  const handleCloseBothy = useCallback(
    () => {
      dispatch(clearSelectedBothy());
    },
    [dispatch]
  );

  const handleSearchChange = useCallback(
    event => {
      dispatch(setSearchTerm(event.target.value));
    },
    [dispatch]
  );

  const handleBothyFeatureToggle = useCallback(
    feature => {
      dispatch(toggleBothyFeatureFilter(feature));
    },
    [dispatch]
  );

  const handleNaturalFeatureToggle = useCallback(
    feature => {
      dispatch(toggleNaturalFeatureFilter(feature));
    },
    [dispatch]
  );

  const handleElevationChange = useCallback(
    (field, value) => {
      dispatch(setElevationFilter({ field, value }));
    },
    [dispatch]
  );

  const handleRoadDistanceChange = useCallback(
    (field, value) => {
      dispatch(setRoadDistanceFilter({ field, value }));
    },
    [dispatch]
  );

  const handleBortleClassChange = useCallback(
    value => {
      dispatch(setBortleClassFilter(value));
    },
    [dispatch]
  );

  const handleWeatherFilterChange = useCallback(
    (field, value) => {
      dispatch(setWeatherFilter({ field, value }));
    },
    [dispatch]
  );

  const handleToggleFilters = useCallback(
    () => {
      dispatch(toggleFilters());
    },
    [dispatch]
  );

  const handleClearFilters = useCallback(
    () => {
      dispatch(clearFilters());
    },
    [dispatch]
  );

  return (
    <div className="bothies-map-shell">
      <header className="hero">
        <div className="hero__copy">
          <p className="eyebrow">Penpidyn Outdoor Club</p>
          <h1>Bothier</h1>
        </div>
      </header>

      <div className="content-grid">
        <section className="panel map-panel">
          <div className="map-shell">
            <MapView
              bothies={visibleBothies}
              selectedBothy={selectedBothy}
              onSelectBothy={handleBothyClick}
            />
          </div>
        </section>

        <aside className="panel info-panel">
          {selectedBothy ? (
            <BothyDetails
              bothy={selectedBothy}
              weatherState={weatherState}
              onClose={handleCloseBothy}
            />
          ) : (
            <BothyBrowser
              bothies={visibleBothies}
              searchValue={searchValue}
              showFilters={showFilters}
              filters={filters}
              visibleBothiesCount={visibleBothies.length}
              allBothiesCount={allBothiesCount}
              availableBothyFeatures={availableBothyFeatures}
              availableBortleClasses={availableBortleClasses}
              naturalFeatureOptions={NATURAL_FEATURE_OPTIONS}
              weatherDayOptions={weatherDayOptions}
              weatherUnits={weatherUnits}
              weatherRanges={weatherRanges}
              onSearchChange={handleSearchChange}
              onToggleFilters={handleToggleFilters}
              onBothyFeatureToggle={handleBothyFeatureToggle}
              onNaturalFeatureToggle={handleNaturalFeatureToggle}
              onElevationChange={handleElevationChange}
              onRoadDistanceChange={handleRoadDistanceChange}
              onBortleClassChange={handleBortleClassChange}
              onWeatherFilterChange={handleWeatherFilterChange}
              onClearFilters={handleClearFilters}
              onSelectBothy={handleBothyClick}
            />
          )}
        </aside>
      </div>

      <footer className="footer">
        <p>
          Data sourced from Mountain Bothies Association | Weather data provided
          by Open-Meteo API
        </p>
      </footer>
    </div>
  );
};

export default BothiesMap;

function parseFiltersFromSearch(searchString) {
  const params = new URLSearchParams(searchString);
  const defaultFilters = createDefaultFilters();
  const filters = {
    ...defaultFilters,
    naturalFeatures: { ...defaultFilters.naturalFeatures }
  };

  let hasParams = false;
  const parseNumber = value => {
    const num = Number(value);
    return Number.isFinite(num) ? num : "";
  };

  const setIfPresent = (key, setter) => {
    const value = params.get(key);
    if (value !== null && value !== "") {
      hasParams = true;
      setter(value);
    }
  };

  setIfPresent("bf", value => {
    filters.bothyFeatures = value.split(",").filter(Boolean);
  });

  setIfPresent("nat", value => {
    value
      .split(",")
      .filter(Boolean)
      .forEach(key => {
        filters.naturalFeatures[key] = true;
      });
  });

  setIfPresent("emin", value => {
    filters.elevation.min = parseNumber(value);
  });
  setIfPresent("emax", value => {
    filters.elevation.max = parseNumber(value);
  });
  setIfPresent("rmin", value => {
    filters.roadDistance.min = parseNumber(value);
  });
  setIfPresent("rmax", value => {
    filters.roadDistance.max = parseNumber(value);
  });
  setIfPresent("bortle", value => {
    filters.bortleClass = parseNumber(value);
  });

  const weatherMap = {
    wDay: "targetDayIndex",
    wMaxPrec: "maxPrecipitation",
    wMaxRain: "maxRainfall",
    wMaxSnow: "maxSnowfall",
    wMinSnow: "minSnowfall",
    wMinDepth: "minSnowDepth",
    wMaxWind: "maxWindSpeed",
    wMaxPrecHrs: "maxPrecipitationHours",
    wMinLow: "minDailyLow",
    wMaxHigh: "maxDailyHigh"
  };

  Object.entries(weatherMap).forEach(([paramKey, field]) => {
    setIfPresent(paramKey, value => {
      filters.weather[field] = parseNumber(value);
    });
  });

  const searchTerm = params.get("q") ?? "";
  if (searchTerm) {
    hasParams = true;
  }

  return {
    filters,
    searchTerm,
    hasParams
  };
}

function buildSearchFromFilters(filters, searchTerm) {
  const params = new URLSearchParams();
  const trimSearch = searchTerm.trim();
  if (trimSearch) {
    params.set("q", trimSearch);
  }

  if (filters.bothyFeatures.length > 0) {
    params.set("bf", filters.bothyFeatures.join(","));
  }

  const activeNatural = Object.entries(filters.naturalFeatures)
    .filter(([, active]) => active)
    .map(([key]) => key);
  if (activeNatural.length > 0) {
    params.set("nat", activeNatural.join(","));
  }

  const numericField = (value, key) => {
    if (value !== "" && value !== null && value !== undefined) {
      params.set(key, String(value));
    }
  };

  numericField(filters.elevation.min, "emin");
  numericField(filters.elevation.max, "emax");
  numericField(filters.roadDistance.min, "rmin");
  numericField(filters.roadDistance.max, "rmax");
  numericField(filters.bortleClass, "bortle");

  const weatherMap = {
    targetDayIndex: "wDay",
    maxPrecipitation: "wMaxPrec",
    maxRainfall: "wMaxRain",
    maxSnowfall: "wMaxSnow",
    minSnowfall: "wMinSnow",
    minSnowDepth: "wMinDepth",
    maxWindSpeed: "wMaxWind",
    maxPrecipitationHours: "wMaxPrecHrs",
    minDailyLow: "wMinLow",
    maxDailyHigh: "wMaxHigh"
  };

  Object.entries(weatherMap).forEach(([field, key]) => {
    numericField(filters.weather[field], key);
  });

  return params.toString();
}
